/*
 * Modules dependencies
 */

import { Carousel, Slide } from 'src/components/core/carousel';
import { Heading } from 'src/components/core/heading';
import { Svg } from 'src/components/core/svg';
import { Text } from 'src/components/core/text';
import { media } from '@untile/react-core/styles/media';
import { transparentize } from 'src/styles/utils/colors';
import iconCheck from 'src/assets/svgs/item-check.svg';
import iconClose from 'src/assets/svgs/item-close.svg';
import styled from 'styled-components';

/**
 * `ListItem` type.
 */

export type ListItem = {
  isActive: boolean;
  title: string;
};

/*
 * Export `Plan` type.
 */

export type Plan = {
  description: string;
  list: ListItem[];
  title: string;
};

/*
 * `Coverage` type.
 */

export type Coverage = {
  description: string;
  plans: Plan[];
  title: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  padding-top: var(--section-space);

  ${media.min.md`
    padding-top: var(--space-ms);
  `}
`;

/*
 * `StyledHeading`  styled component.
 */

const StyledHeading = styled(Heading).attrs({
  asDescription: 'p',
  asTitle: 'h4',
  descriptionSize: 14,
  descriptionTheme: 'slate300',
  titleSize: 22
})`
  padding: 0 var(--space-sm);

  ${media.min.md`
    padding: 0 var(--space-xs);
  `}
`;

/*
 * `StyledCarousel` styled component.
 */

const StyledCarousel = styled(Carousel)`
  margin-top: var(--space-xxs);
  overflow: visible;
  padding: 0 var(--space-xxs);

  ${media.max.ms`
    .swiper-slide {
      width: 264px;
    }
  `}
`;

/**
 * `PlanCard` styled component.
 */

const PlanCard = styled.div`
  background-color: ${transparentize('slate800', 0.16)};
  border-radius: var(--border-radius-xxs);
  padding: var(--space-xs);

  div p {
    font-weight: 600 !important;
  }
`;

/**
 * `ListItem` styled component.
 */

const ListItem = styled.li`
  column-gap: var(--space-xxs);
  display: grid;
  grid-template-columns: 20px 1fr;
  list-style: none;
  padding: 2px 0;
`;

/**
 * Export `Coverages` component.
 */

export const Coverages = ({ plans, ...rest }: Coverage) => {
  return (
    <Wrapper>
      <StyledHeading {...rest} />

      <StyledCarousel>
        {plans.map(({ description, list, title }, index) => (
          <Slide key={index}>
            <PlanCard>
              <Heading
                asDescription={'p'}
                description={description}
                descriptionSize={14}
                isDescriptionRaw
                label={title}
                labelSize={14}
              />

              <ul>
                {list.map(({ isActive, title }, index) => (
                  <ListItem key={index}>
                    <Svg icon={isActive ? iconCheck : iconClose} size={'20px'} style={{ paddingTop: '1px' }} />
                    <Text
                      style={{ color: isActive ? 'var(--color-text)' : 'var(--color-slate500)' }}
                      variant={'labelM'}
                    >
                      {title}
                    </Text>
                  </ListItem>
                ))}
              </ul>
            </PlanCard>
          </Slide>
        ))}
      </StyledCarousel>
    </Wrapper>
  );
};
